import React from 'react';

import { css } from '@emotion/react';
import tinycolor from 'tinycolor2';

import { WebinarDimensions, withWebinarPlayerContext } from '../context';
import { useThemeContext } from '../../ui/ThemeContext';
import {
  MaxScaledRoomWidth,
  ScaleRoomDimensions,
  useScaledRoomScale,
  useVideoStageAspectScale,
} from '../useScaledRoomScale';

import { InteractionInfo } from '../../Interactions/InteractionInfo';

import { VideoStageControls } from '../Controls/VideoStageControls';
import { OverlayInteractionsParent } from '../Controls/OverlayInteractionsParent';
import { useRoomArea } from './RoomHooks';
import { AnimatePresence, motion } from 'framer-motion';
import { CustomerLogoAndTitle, PoweredByFooter } from './RoomPartsBase';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { PresentationRoomStage } from './PresentationRoomParts';
import {
  SSOLoginButtonInRoom,
  useSSOAuth,
} from '$/sections/Home/Profile/TeamIntegrations/Info/SSO/SSOLogin';
import { HasJoined, PlayerMode, PlayerPlacement } from '../types';
import { t, Trans } from '@lingui/macro';
import { logger } from '$/utils/logger';
import { useInactiveReplay } from '../useInactiveReplay';
import { JoiningErrorBox, OverVideoErrorBox } from '../PlayerError';
import { InRoomButton } from '$/components/ui/Button/InRoomButton';
import { RoomType } from '$/GraphQL/schema/types';
import { TestControlBar } from '../TestControlBar/TestControlBar';
import { WaitFor } from '$/components/DelayRender';
import { Helmet } from 'react-helmet';
import { PlayerOverlay } from './PlayerOverlay';
import { useVideoStageArea } from '$/hooks/useVideoStageArea';
import isMobile from '$/utils/isMobile';

export const useRoomGradient = (primaryColor: string, dimensions: WebinarDimensions): string => {
  const etheme = useThemeContext();
  const lightPrimaryColor = tinycolor(primaryColor).lighten(95);

  return `
    background: ${etheme.player.bg};
    background: linear-gradient(180deg, ${lightPrimaryColor} 0%, #ffffff calc(100% - ${dimensions.videoControlsArea.bottomBar.height}rem), rgba(255,255,255,1) 100%);
    color: ${etheme.player.fg};
  `;
};

interface OverlaysOverAreaProps {
  fullScreen?: boolean;
  overlaysOverArea: InteractionInfo[];
}

const OverlaysOverArea: React.FC<OverlaysOverAreaProps> = withWebinarPlayerContext(
  ['interactionInEdit'],
  'OverlaysOverArea'
)(({ overlaysOverArea, context: { interactionInEdit } }) => {
  return (
    <div
      className='z-player-roomarea-overlay'
      css={css`
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;

        pointer-events: none;
        > div {
          pointer-events: all;
        }
      `}
    >
      <OverlayInteractionsParent
        key='overlays'
        interactionInEdit={interactionInEdit}
        items={overlaysOverArea}
        className='z-player-room-overlay'
      />
    </div>
  );
});

export const ScaledRoomFooter: React.FC<{
  Footer: React.FC;
  roomDimensions: ScaleRoomDimensions;
}> = withWebinarPlayerContext(['transitions'])(({
  Footer,
  roomDimensions,
  context: { transitions },
}) => {
  const scaledPartTransform = React.useMemo(() => {
    return {
      scale: roomDimensions.scale,
      x: '0px',
      y: '0px',
      opacity: roomDimensions.isMeasured ? 1 : 0,
    };
  }, [roomDimensions.isMeasured, roomDimensions.scale]);

  return (
    <motion.div
      ref={roomDimensions.footerSize.ref}
      key='FooterPart'
      className='ScaledPart footer'
      css={css`
        bottom: 0;
        transform-origin: bottom center;
        margin-bottom: 1rem;
      `}
      transition={transitions['video.resize']}
      initial={{ ...scaledPartTransform, y: '150%' }}
      animate={scaledPartTransform}
      exit={{ y: '150%' }}
    >
      <Footer />
    </motion.div>
  );
});

type ScaledRoomContextType = Pick<ScaleRoomDimensions, 'scale'>;
const ScaledRoomContext = React.createContext<ScaledRoomContextType>({ scale: 1 });
export const useScaledRoomContext = (): ScaledRoomContextType => {
  return React.useContext(ScaledRoomContext);
};

export const ScaledRoomWrap: React.FC<{
  fullScreen?: boolean;
  roomDimensions: ScaleRoomDimensions;
  overlay?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = withWebinarPlayerContext(['dimensions', 'transitions'])(({
  fullScreen,
  footer,
  overlay,
  roomDimensions,
  context: { dimensions, transitions },
  children,
}) => {
  const xPx =
    ((fullScreen ? dimensions.windowArea.width : dimensions.roomArea.width) - MaxScaledRoomWidth) /
    2;

  const [scaledPartTransform, scaledPartTransformStyle] = React.useMemo(() => {
    const scaledPartTransform = {
      scale: roomDimensions.scale,
      x: '0px',
      y:
        ((fullScreen ? dimensions.windowArea.height : dimensions.roomArea.height) -
          (roomDimensions.footerSize.height + roomDimensions.bodySize.height) *
            roomDimensions.scale) /
          2 -
        (dimensions.isMeasured ? 0 : 15), // Add a little 15px motion when first showing join room
      opacity: roomDimensions.isMeasured ? 1 : 0,
    };
    return [scaledPartTransform, { ...scaledPartTransform, opacity: 0 }];
  }, [
    dimensions.isMeasured,
    dimensions.roomArea.height,
    dimensions.windowArea.height,
    fullScreen,
    roomDimensions.bodySize.height,
    roomDimensions.footerSize.height,
    roomDimensions.isMeasured,
    roomDimensions.scale,
  ]);

  const height = fullScreen ? dimensions.windowArea.height : dimensions.roomArea.height;

  const scaledRoomHeight = React.useMemo(() => {
    return {
      height: `${height}px`,
      width: `${dimensions.roomArea.width}px`,
    };
  }, [dimensions.roomArea.width, height]);

  const scaledRoomX = React.useMemo(() => {
    return {
      x: `${xPx}px`,
    };
  }, [xPx]);

  const context = React.useMemo(() => {
    return { scale: roomDimensions.overlayScale };
  }, [roomDimensions.overlayScale]);

  return (
    <ScaledRoomContext.Provider value={context}>
      <motion.div
        className='ScaledRoom room-font flex z-player-roomarea-overlay'
        css={css`
          display: ${dimensions.isMeasured ? 'flex' : 'none'};
          position: absolute;
          top: ${fullScreen ? 0 : dimensions.roomArea.top}px;
          left: 0;
          pointer-events: none;

          .ScaledPart {
            position: absolute;
            left: 0;
            width: ${MaxScaledRoomWidth}px;
          }

          .header,
          .body,
          .footer {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            > * {
              pointer-events: auto;
            }
          }

          .header {
            justify-content: flex-end;
            align-items: center;
            flex-grow: 1;
          }

          .body {
            justify-content: flex-start;
            flex-grow: 1;
          }

          .footer {
            transform-origin: bottom;
            justify-content: flex-center;
            align-items: center;

            min-height: ${dimensions.videoControlsArea.bottomBar.height / roomDimensions.scale -
            dimensions.videoControlsArea.bottomBar.paddingY * 2}px;
            bottom: ${dimensions.videoControlsArea.bottomBar.paddingY}px;
          }

          .scaled-overlay {
            .aspect-scale,
            .scale-control {
              transition: transform ${transitions['video.resize'].duration}s;
              transform: scale(${roomDimensions.scale});
            }

            .scale-overlay {
              transition: transform ${transitions['video.resize'].duration}s;
              transform: scale(${roomDimensions.overlayScale});
            }
          }
        `}
        transition={transitions['video.resize']}
        initial={scaledRoomHeight}
        animate={scaledRoomHeight}
      >
        <motion.div
          css={css`
            width: ${MaxScaledRoomWidth}px;
          `}
          initial={scaledRoomX}
          animate={scaledRoomX}
        >
          <AnimatePresence>
            <motion.div
              ref={roomDimensions.bodySize.ref}
              key='BodyPart'
              className='ScaledPart flex flex-col flex-1'
              css={css`
                top: 0;
                transform-origin: top;
                margin-bottom: 1rem;
              `}
              transition={{ ...transitions['video.resize'] }}
              initial={scaledPartTransformStyle}
              animate={roomDimensions.isMeasured ? scaledPartTransform : undefined}
            >
              {children}
            </motion.div>
          </AnimatePresence>
          {footer}
        </motion.div>

        {overlay && <div className='scaled-overlay'>{overlay}</div>}
      </motion.div>
    </ScaledRoomContext.Provider>
  );
});

export const ScaledRoom: React.FC<{
  Header: React.FC | null;
  Body: React.FC | null;
  Footer: React.FC | null;
  fullScreen?: boolean;
  overlaysOverArea?: InteractionInfo[];
}> = withWebinarPlayerContext(
  ['dimensions'],
  'Scaledroom'
)(({ Header, Body, Footer, fullScreen, overlaysOverArea, context: { dimensions } }) => {
  const scaledRoomDimensions = useScaledRoomScale(fullScreen, dimensions);

  return (
    <>
      <ScaledRoomWrap
        fullScreen={fullScreen}
        roomDimensions={scaledRoomDimensions}
        overlay={
          overlaysOverArea && overlaysOverArea.length > 0 ? (
            /* These go into Footer so they are scaled but not manipulated like Header/Body */
            <OverlaysOverArea fullScreen={fullScreen} overlaysOverArea={overlaysOverArea} />
          ) : undefined
        }
        footer={
          Footer ? (
            <ScaledRoomFooter Footer={Footer} roomDimensions={scaledRoomDimensions} />
          ) : undefined
        }
      >
        <div className='header'>{Header && <Header />}</div>
        <div className='body'>{Body && <Body />}</div>
      </ScaledRoomWrap>
    </>
  );
});

type SegregatedInteractions = {
  overlaysUnderControls: InteractionInfo[];
  overlaysOverControls: InteractionInfo[];
};

const VideoStage: React.FC<{
  Stage: React.FC | null | undefined;
  zIndex?: number;
  overlayInteractions: SegregatedInteractions;
}> = withWebinarPlayerContext(
  [
    'currentRoom',
    'editMode',
    'dimensions',
    'interactionInEdit',
    'animations',
    'transitions',
    'showOverlay',
    'pinnedInteraction',
  ],
  'VideoStage'
)(({ Stage, zIndex, overlayInteractions, context }) => {
  const {
    currentRoom,
    editMode,
    dimensions,
    interactionInEdit,
    animations,
    transitions,
    showOverlay,
    pinnedInteraction,
  } = context;

  const { overlaysUnderControls, overlaysOverControls } = overlayInteractions;
  const videoStageAspectScale = useVideoStageAspectScale(dimensions);

  React.useEffect(() => {
    if (!dimensions.isMeasured) return;

    window.setTimeout(() => {
      animations.start('video.resize');
    }, 250);
  }, [animations, dimensions.isMeasured]);

  const [style, animateStyle] = useVideoStageArea({
    animations,
    currentRoom,
    dimensions,
    pinnedInteraction,
  });

  return (
    <motion.div
      id='VideoStage'
      key='VideoStage'
      className={`
        VideoStage z-player-video-stage room-font
        flex flex-col justify-center items-center overflow-hidden
      `}
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        transform-origin: top left;
        opacity: 0;
        background: #fff;

        .w-full-control {
          width: ${(1 / videoStageAspectScale) * 100}%;
        }

        .aspect-scale,
        .scale-control {
          transition: transform 1.25s ease-in;
          transform: scale(${videoStageAspectScale});
          pointer-events: auto;

          > .aspect-scale,
          > .scale-control {
            transform: unset !important;
          }
        }
      `}
      transition={showOverlay ? { duration: 0 } : transitions['video.resize']}
      initial={style}
      animate={animateStyle}
      exit={{
        y: '-100vh',
        transition: { duration: 1.5 },
      }}
    >
      <AnimatePresence>
        {(currentRoom.caps.hasVideoPlayer || editMode) && (
          <PresentationRoomStage key='stage-video' />
        )}
        {Stage && (
          <motion.div
            key='stage-room'
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;

              ${zIndex ? `z-index: ${zIndex};` : ''}
            `}
            transition={transitions['video.curtain']}
            initial={{ y: '0%' }}
            exit={{ y: '-100%' }}
          >
            <Stage />
          </motion.div>
        )}
      </AnimatePresence>

      <OverlayInteractionsParent
        interactionInEdit={interactionInEdit}
        items={overlaysUnderControls}
        className='z-player-non-clickable-overlay'
      />

      <div
        id='formOverVideoContainer'
        className='z-player-control-bar'
        css={css`
          visibility: visible;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none !important;
        `}
      />

      <VideoStageControls overlayItemsOverControls={overlaysOverControls} />
    </motion.div>
  );
});

export const RoomParts: React.FC = withWebinarPlayerContext(
  [
    'interactionInEdit',
    'currentRoom',
    'overlaysOverArea',
    'overlaysOverControls',
    'overlaysUnderControls',
  ],
  'RoomParts'
)(({
  context: {
    overlaysOverArea,
    overlaysOverControls,
    overlaysUnderControls,
    currentRoom,
    interactionInEdit,
  },
}) => {
  const roomCurrent = useRoomArea(currentRoom.type);

  const { Header, Body, Footer, Stage, stageZIndex } = React.useMemo(() => {
    const roomOrCurrentRoom = roomCurrent;
    const stage = roomOrCurrentRoom.stageComponent();
    return {
      Header: roomOrCurrentRoom.headerComponent(),
      Body: roomOrCurrentRoom.bodyComponent(),
      Footer: roomOrCurrentRoom.footerComponent(),
      Stage: stage?.component,
      stageZIndex: stage?.zIndex,
    };
  }, [roomCurrent]);

  const hasDragElementsEdit =
    interactionInEdit && interactionInEdit.hasVideoOverlay() === 'over-area';

  const segregatedOverlayInteractions = React.useMemo(() => {
    return {
      overlaysUnderControls,
      overlaysOverControls,
    };
  }, [overlaysOverControls, overlaysUnderControls]);

  return (
    <AnimatePresence>
      <ScaledRoom
        key='room'
        Header={Header}
        Body={Body}
        Footer={Footer}
        overlaysOverArea={overlaysOverArea}
      />

      <AnimatePresence>
        {(currentRoom.caps.hasVideoPlayer || Stage) && (
          <VideoStage
            key='stage'
            Stage={Stage}
            zIndex={stageZIndex}
            overlayInteractions={segregatedOverlayInteractions}
          />
        )}
      </AnimatePresence>

      {/* -- This is used for editing -- */}
      {hasDragElementsEdit && (
        <div
          id='dragElementsEdit'
          key='edit'
          className='overflow-hidden z-player-drag-edit'
          css={css`
            pointer-events: none;
            * {
              pointer-events: auto;
            }
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
          `}
        />
      )}
    </AnimatePresence>
  );
});

export const JoinRoom: React.FC<{ children?: React.ReactNode }> = withWebinarPlayerContext(
  [
    'currentRoom',
    'clickedJoin',
    'error',
    'setError',
    'errorCode',
    'loaded',
    'hasJoined',
    'set',
    'attendeeId',
    'animations',
    'dimensions',
    'lastActiveDateRef',
    'isPlaying',
    'mode',
    'isReplayExpired',
    'inactiveReplay',
    'onRejoinReplay',
    'onInactiveReplay',
    'ewebinar',
    'pauseTimer',
    'joinMessage',
    'placement',
    'showOverlay',
    'dimensions',
  ],
  'JoinRoom'
)(({
  children,
  context: {
    currentRoom,
    clickedJoin,
    error,
    setError,
    errorCode,
    loaded,
    hasJoined,
    set,
    animations,
    dimensions,
    lastActiveDateRef,
    isPlaying,
    mode,
    isReplayExpired,
    inactiveReplay,
    onRejoinReplay,
    onInactiveReplay,
    ewebinar,
    pauseTimer,
    attendeeId,
    joinMessage,
    placement,
    showOverlay,
  },
}) => {
  const { authorized } = useSSOAuth(set, attendeeId);

  React.useEffect(() => {
    if (hasJoined !== HasJoined.Joining) return;

    const timer = setTimeout(() => {
      setError((error) => error ?? t`Connecting to the webinar is taking longer than usual.`);
      logger.error('Connecting to the webinar is taking longer than usual');
    }, 20 * 1000);

    return (): void => {
      timer && clearTimeout(timer);
    };
  }, [hasJoined, setError]);

  const showJoinButton =
    !showOverlay &&
    mode === PlayerMode.live &&
    hasJoined !== HasJoined.Joined &&
    currentRoom.caps.hasVideoPlayer;

  const { inactive: showInactiveReplayButton, rejoin } = useInactiveReplay({
    lastActiveDateRef,
    isPlaying,
    mode,
    isReplayExpired,
    inactiveReplay,
    onRejoinReplay,
    onInactiveReplay,
    ewebinar,
    pauseTimer,
  });

  const showButtonChanged = useHasChangedShallow([showJoinButton, showInactiveReplayButton]);
  React.useEffect(() => {
    if (!showButtonChanged) return;

    let animate = 'join.contents.show';
    if (showJoinButton || showInactiveReplayButton) {
      animate = 'join.contents.hide';
    }

    logger.debug('ANIM', 'start ', [animate]);
    animations.start(animate);
  }, [animations, showButtonChanged, showInactiveReplayButton, showJoinButton]);

  // A copy of this boolean is in VideoStageControls - if this changes also change that copy
  const showOverVideoError = error && hasJoined !== HasJoined.Joining;

  const JoinBody = React.useMemo(() => {
    return (): JSX.Element => {
      if (!showJoinButton) {
        return (
          <>
            <div className='mb-2e'>
              <Trans>Replay disconnected due to inactivity.</Trans>
            </div>
            <InRoomButton onClick={rejoin} submitting={false}>
              <Trans>Rejoin replay</Trans>
            </InRoomButton>
          </>
        );
      }

      return (
        <>
          {showOverVideoError ? (
            <OverVideoErrorBox className='static' error={error} errorCode={errorCode} mode={mode} />
          ) : authorized ? (
            <>
              <InRoomButton
                submitting={!loaded || hasJoined === HasJoined.Joining}
                onClick={clickedJoin}
              >
                {currentRoom.type === RoomType.Waiting ? (
                  <Trans>Enter waiting room</Trans>
                ) : (
                  <Trans>Join webinar</Trans>
                )}
              </InRoomButton>
              {joinMessage && (
                <div
                  className='md:text-lg max-w-screen md:w-40e p-1_5e  mt-2e text-center'
                  css={css`
                    line-height: 2rem;
                  `}
                >
                  {joinMessage}
                </div>
              )}
              <JoiningErrorBox error={error} errorCode={errorCode} mode={mode} />
            </>
          ) : (
            <SSOLoginButtonInRoom set={set} attendeeId={attendeeId} />
          )}
        </>
      );
    };
  }, [
    attendeeId,
    authorized,
    clickedJoin,
    currentRoom.type,
    error,
    errorCode,
    hasJoined,
    joinMessage,
    loaded,
    mode,
    rejoin,
    set,
    showJoinButton,
    showOverVideoError,
  ]);

  const gradientCss = useRoomGradient(ewebinar.primaryColor, dimensions);

  return (
    <>
      {(showJoinButton || showInactiveReplayButton) && (
        <div
          id='JoinRoom'
          className={`absolute top-0 bottom-0 right-0 left-0 flex flex-col justify-center items-center z-player-join`}
          css={css`
            font-family: ${ewebinar.font ?? 'DM Sans'};
            ${gradientCss}
          `}
        >
          <Helmet>
            <style>
              {`body {
                    background-color: ${
                      placement === PlayerPlacement.EmbeddedPlayer ? 'transparent' : '#fff'
                    } !important;
                  }
                `}
            </style>
          </Helmet>
          <WaitFor element='testControlBar'>
            <TestControlBar />
          </WaitFor>

          <ScaledRoom
            fullScreen
            Header={CustomerLogoAndTitle}
            Body={JoinBody}
            Footer={PoweredByFooter}
          />
        </div>
      )}

      <PlayerOverlay />

      <motion.div
        id='JoinParent'
        className={`
          join-parent flex relative
          ${mode === PlayerMode.edit ? 'w-full h-full' : ''}
        `}
        css={css`
          ${showOverVideoError
            ? `
                pointer-events: none;
                iframe {
                  pointer-events: auto;
                }
                `
            : ``}
        `}
        transition={{
          duration: isMobile ? 0 : 0.25,
        }}
        animate={{
          width: mode === PlayerMode.edit ? undefined : dimensions.webinarArea.width,
          height: mode === PlayerMode.edit ? undefined : dimensions.webinarArea.height,
        }}
        variants={{
          'join.contents.hide': { opacity: 0 },
          'join.contents.show': { opacity: 1 },
        }}
      >
        <WaitFor element='testControlBar'>
          <TestControlBar />
        </WaitFor>

        {children}
      </motion.div>
    </>
  );
});
